import { AppDispatch } from "@lib/stores";
import { ContentActions } from "@lib/stores/content/slices";
import * as Api from "@modules/api";
import { cacheExpired } from "@modules/local/helpers";
import { uiEntitiesRepository } from "@modules/local/repositories/uiEntitiesRepository";
import { EmptyParameters } from "../types";

export const defaultPageSize = 10;

export const getContent = async (
  {
    context,
    dispatch,
    pageNumber,
    pageSize,
    uniqueId,
    selectorsData,
    criteria,
    relationLength = 1,
  }: {
    dispatch: AppDispatch;
    selectorsData?: {
      selectors?: Api.Server.ComponentContentEntitySelectorOutput[];
      sorters?: Api.Server.ComponentContentEntitySorterOutput[];
    };
    uniqueId: string;
    context: Api.Server.ContentEntityOutput | undefined | null;
    pageNumber: number | undefined;
    pageSize: number | undefined;
    criteria?: string;
    relationLength?: number;
  },
  forceFetch?: boolean
) => {
  if (!selectorsData) return;
  if (isContentEntitySelector(selectorsData, context)) {
    if (context) {
      // await db.uiEntities.where('uniqueId').equals(uniqueId).delete();
      await uiEntitiesRepository.put({
        uniqueId: uniqueId,
        contentIds: [context.id!],
        hasNextPage: false,
        pageNumber: 1,
        pageSize: pageSize ?? defaultPageSize,
        totalCount: 1,
      });
    }

    // await dispatch(ContentActions.setLinkedContent({ uniqueId, content: context }));
    return;
  }

  const uiInfo = await uiEntitiesRepository.get(uniqueId);

  if (!forceFetch && !cacheExpired(uiInfo)) return;

  console.log("gonna dispatch" + uniqueId);
  await dispatch(
    ContentActions.getContentEntityBySelectorsAndSortersAsync({
      input: {
        selectors:
          selectorsData.selectors?.map((d) => ({
            factor: d.factor,
            criteria: d.defaultCriteria ?? criteria,
            entityId: d.defaultEntityId ?? context?.id,
            relationLength: d.defaultRelationLength ?? relationLength,
            typeId: d.defaultTypeId ?? context?.contentEntityTypeId,
          })) || [],
        sorters:
          selectorsData?.sorters?.map((s) => ({
            factor: s.factor,
            direction: s.direction,
          })) || [],
        pageNumber: pageNumber ? pageNumber : 1,
        pageSize: pageSize ? pageSize : defaultPageSize,
      },
      uniqueId: uniqueId,
    })
  );
};

export const getMoreContent = async ({
  context,
  dispatch,
  uniqueId,
  selectorsData,
  criteria,
  relationLength = 1,
}: {
  dispatch: AppDispatch;
  selectorsData?: {
    selectors?: Api.Server.ComponentContentEntitySelectorOutput[];
    sorters?: Api.Server.ComponentContentEntitySorterOutput[];
  };
  uniqueId: string;
  context: Api.Server.ContentEntityOutput | undefined | null;
  criteria?: string;
  relationLength?: number;
}) => {
  if (!selectorsData) return;

  console.log("gonna dispatch" + uniqueId);
  await dispatch(
    ContentActions.getMoreContentEntityBySelectorsAndSortersAsync({
      input: {
        selectors:
          selectorsData.selectors?.map((d) => ({
            factor: d.factor,
            criteria: d.defaultCriteria ?? criteria,
            entityId: d.defaultEntityId ?? context?.id,
            relationLength: d.defaultRelationLength ?? relationLength,
            typeId: d.defaultTypeId ?? context?.contentEntityTypeId,
          })) || [],
        sorters:
          selectorsData?.sorters?.map((s) => ({
            factor: s.factor,
            direction: s.direction,
          })) || [],
      },
      uniqueId: uniqueId,
    })
  );
};

export function isContentEntitySelector(
  selectorsData: {
    selectors?: Api.Server.ComponentContentEntitySelectorOutput[];
    sorters?: Api.Server.ComponentContentEntitySorterOutput[];
  },
  context: Api.Server.ContentEntityOutput | null | undefined
) {
  return (
    (selectorsData.selectors?.length ?? 0) === 0 ||
    (!(selectorsData.selectors ?? []).find(
      (f) =>
        !(
          f.factor == Api.Server.SelectFactor.ContentId &&
          f.defaultEntityId === undefined
        )
    ) &&
      context != undefined)
  );
}

export function getUniqueId(
  type: "page" | "component",
  uiId: string | undefined,
  contentId: string | undefined,
  contentEntityTypeId: number | undefined,
  criteria: string | undefined
): string {
  return `${type}:${uiId ?? ""}:${contentId ?? ""}:${
    contentEntityTypeId ?? ""
  }:${criteria ?? ""}`;
}

export function getInfoFromUniqueId(uniqueId: string | undefined) {
  const splits = (uniqueId ?? "::::")?.split(":");

  const type = splits[0] ?? undefined;
  const uiId = splits[1] ?? undefined;
  const contentId = splits[2] ?? undefined;
  const contentEntityTypeId = splits[3] ?? undefined;
  const criteria = splits[4] ?? undefined;

  return {
    type: type == "" ? undefined : type,
    uiId: uiId == "" ? undefined : uiId,
    contentId: contentId == "" ? undefined : contentId,
    contentEntityTypeId:
      contentEntityTypeId == "" ? undefined : parseInt(contentEntityTypeId),
    criteria: criteria == "" ? undefined : criteria,
  };
}

export const getComponentParameterValues = <
  TItemParameters extends EmptyParameters,
>(
  keys: (keyof TItemParameters)[],
  component: Api.Server.ComponentOutput | undefined,
  contents: Api.Server.ContentEntityOutput[]
): {
  content: Api.Server.ContentEntityOutput;
  values: { [K in keyof TItemParameters]?: TItemParameters[K] };
}[] => {
  const itemTemplate = component;
  if (itemTemplate === undefined) {
    return (
      contents?.map((c) => ({
        content: c,
        values: {},
      })) ?? []
    );
  }

  return (
    contents?.map((c) => ({
      content: c,
      values: keys.reduce(
        (acc, key) => ({
          ...acc,
          [key]: processParameterValue<TItemParameters, keyof TItemParameters>(
            key,
            itemTemplate,
            c
          ),
        }),
        {} as { [K in keyof TItemParameters]: TItemParameters[K] }
      ),
    })) ?? []
  );
};

export function processParameterValue<
  TParameters,
  TKey extends keyof TParameters,
>(
  key: TKey,
  component: Api.Server.ComponentWithUniqueIdOutput,
  context?: Api.Server.ContentEntityOutput
): TParameters[keyof TParameters] | undefined {
  if (
    component.parameters === undefined ||
    component.parameters[key as string] === undefined
  )
    return;
  const parameter = component.parameters[key as string];
  const value = ((parameter as any)?.fullUrlAlternative ??
    parameter.valueV2 ??
    parameter.value) as TParameters[TKey];
  switch (parameter.fieldType) {
    case Api.Server.ComponentFieldType.MappedField:
      return getMappedFieldValue(parameter, context) as TParameters[TKey];
    case Api.Server.ComponentFieldType.PredefinedComponent:
      return value;
    case Api.Server.ComponentFieldType.Primitive:
      return value;
    case Api.Server.ComponentFieldType.RequiredComponent:
      return parameter.id as TParameters[TKey];
  }
  return;
}

export function getMappedFieldValue(
  field: Api.Server.ComponentFieldOutput,
  context?: Api.Server.ContentEntityOutput
) {
  if (context === undefined) return null;
  const fieldName = field.mappingOptions?.find(
    (f) => f.contentEntityTypeId === context?.contentEntityTypeId
  )?.entityTypeField;
  if (fieldName === undefined) return null;
  const parameter = context?.parameters?.find((g) => g.key === fieldName);
  return parameter?.valueV2 ?? parameter?.value ?? null;
}
